import { useEffect, useMemo, useRef, useState } from "react";
import { useBackLayer } from "@hybrid-iptv/ui";
import type { Episode, SeriesItem } from "@hybrid-iptv/types";
import { useActiveProviderSourceId } from "../epg/api.js";
import type { CatalogQuery } from "../library/catalogQuery.js";
import { UP_NEXT_SECONDS, episodeAfter, parseEpisodeKey, upNextPhase } from "../library/episodeSequence.js";
import { episodeName } from "../library/seriesEpisodes.js";
import { useProgressStore } from "../library/progressStore.js";
import { useCatalogQuery } from "../library/useCatalogQuery.js";
import { useSeriesDetails } from "../library/vodHooks.js";
import { effectiveDuration } from "./duration.js";
import { playEpisode } from "./playEpisode.js";
import { usePlayerStore } from "./playerStore.js";

/**
 * Play next (owner, 2026-10-13: "series need a play next button, make it appear automatically with
 * 15 seconds left, then autoplay the next episode when the timer is up").
 *
 * For an episode that has another after it, the last 15 seconds show a card: which episode is next,
 * a countdown in words, and "Play next" (focused, so OK plays it at once). When the time is up, or
 * the video ends, the next episode starts by itself. Back puts the card away and keeps watching,
 * and it stays away for that episode. Works wherever the episode was started from: the next one is
 * found from the progress key of what is playing.
 */


interface NextEpisode {
  episode: Episode;
  seriesTitle: string;
  /** The provider's id for the series, remembered so the next one can be played with it too. */
  providerId?: string;
}

/** "Show Name S1E4" -> "Show Name": the label the series page and Play next give an episode. */
export function seriesTitleOf(label: string | undefined): string | null {
  const title = label?.replace(/\s+S\d+\s*E\d+\s*$/i, "").trim();
  return title ? title : null;
}

/** The next episode of the one playing, with its series' title; null when there is none to play. */
function useNextEpisode(): { next: NextEpisode | null; why: string } {
  const sourceId = useActiveProviderSourceId();
  const content = usePlayerStore((state) => state.playingContent);
  const playingKey = content?.contentType === "episode" ? content.contentRef : null;
  const ref = parseEpisodeKey(playingKey);

  // Which series: what the series page remembered at play time (exact), else found in the catalog
  // by its title (an episode started from Continue watching before it was ever remembered).
  const meta = useProgressStore((state) => (playingKey ? state.metaOf(playingKey) : undefined));
  const rememberedId = meta?.series?.providerId;
  const title = meta?.series?.title ?? seriesTitleOf(meta?.title);
  const lookup = useMemo<CatalogQuery>(() => ({ kind: "series", text: title ?? "", limit: 12 }), [title]);
  const found = useCatalogQuery<SeriesItem>(sourceId, lookup, ref !== null && !rememberedId && title !== null);
  const matched = useMemo(
    () => found.data?.items.find((item) => item.rawTitle.trim().toLowerCase() === (title ?? "").trim().toLowerCase()),
    [found.data, title],
  );
  const providerId = rememberedId ?? matched?.providerId;
  const details = useSeriesDetails(sourceId, providerId);
  const seasons = details.data?.seasons ?? matched?.seasons;
  const season = ref?.season;
  const episode = ref?.episode;
  return useMemo(() => {
    if (season === undefined || episode === undefined) return { next: null, why: "not an episode" };
    if (!providerId && !matched) return { next: null, why: found.isLoading ? "looking up the series" : "series not found" };
    if (!seasons) return { next: null, why: details.isLoading ? "loading the episodes" : "no episode list" };
    const next = episodeAfter(seasons, season, episode);
    if (!next) return { next: null, why: "no episode after this one" };
    return {
      next: { episode: next, seriesTitle: title ?? matched?.rawTitle ?? "", ...(providerId ? { providerId } : {}) },
      why: "ready",
    };
  }, [season, episode, providerId, matched, seasons, title, found.isLoading, details.isLoading]);
}

/**
 * The card for whatever is playing. It asks for nothing unless an episode is playing: live TV and
 * films never touch the catalog from here (and the player renders without any data provider).
 */
export function UpNext() {
  const isEpisode = usePlayerStore((state) => state.playingContent?.contentType === "episode" && state.source?.type === "vod");
  return isEpisode ? <UpNextForEpisode /> : null;
}

function UpNextForEpisode() {
  const source = usePlayerStore((state) => state.source);
  const status = usePlayerStore((state) => state.status);
  const currentTime = usePlayerStore((state) => state.currentTime);
  const duration = usePlayerStore((state) => state.duration);
  const content = usePlayerStore((state) => state.playingContent);
  const { next, why } = useNextEpisode();
  const playingKey = content?.contentType === "episode" ? content.contentRef : null;
  return (
    <>
      <UpNextLayer
        next={next}
        playingKey={playingKey}
        isVod={source?.type === "vod"}
        status={status}
        currentTime={currentTime}
        duration={effectiveDuration(source, duration)}
        play={(seriesTitle, nextEpisode, providerId) => playEpisode(seriesTitle, nextEpisode, providerId)}
      />
      {/* Why there is no card, for support: read through the remote tools, never shown. */}
      {source?.type === "vod" && (
        <span hidden data-up-next={why} data-up-next-key={playingKey ?? "none"} data-up-next-content={content?.contentType ?? "none"} />
      )}
    </>
  );
}

export interface UpNextLayerProps {
  next: NextEpisode | null;
  /** The progress key of the episode playing (it changes when the next one starts). */
  playingKey: string | null;
  isVod: boolean;
  status: string;
  currentTime: number;
  /** The episode's length in seconds (player/duration.ts). */
  duration: number;
  play: (seriesTitle: string, episode: Episode, providerId?: string) => Promise<void> | void;
}

/** The card and its timing, driven by props so every rule is testable without a player. */
export function UpNextLayer({ next, playingKey, isVod, status, currentTime, duration, play }: UpNextLayerProps) {
  // Put away for the episode that is playing; the next one starts fresh.
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const dismissed = dismissedFor !== null && dismissedFor === playingKey;

  const phase = upNextPhase({ hasNext: next !== null && isVod, dismissed, status, currentTime, duration });

  // The time is up: start the next episode, once for this one.
  const started = useRef<string | null>(null);
  useEffect(() => {
    if (phase !== "go" || !next || !playingKey) return;
    if (started.current === playingKey) return;
    started.current = playingKey;
    void play(next.seriesTitle, next.episode, next.providerId);
  }, [phase, next, playingKey, play]);

  const button = useRef<HTMLButtonElement>(null);
  const showing = phase === "countdown" && next !== null;
  useEffect(() => {
    if (showing) button.current?.focus({ preventScroll: true });
  }, [showing]);

  // Back puts the card away and carries on watching: the top layer closes first.
  useBackLayer(showing, () => setDismissedFor(playingKey));

  if (!showing || !next) return null;
  const left = Math.max(1, Math.ceil(duration - currentTime));
  const label = `S${next.episode.seasonNumber} E${next.episode.episodeNumber}`;
  return (
    <div className="up-next" role="group" aria-label="Next episode">
      <p className="up-next__label">Next episode · {label}</p>
      <p className="up-next__title">{episodeName(next.episode)}</p>
      <p className="up-next__count" aria-live="polite">
        Playing in {Math.min(left, UP_NEXT_SECONDS)} s
      </p>
      <button
        ref={button}
        type="button"
        className="ui-btn ui-btn--primary up-next__play"
        onClick={() => {
          started.current = playingKey;
          void play(next.seriesTitle, next.episode, next.providerId);
        }}
      >
        ▶ Play next
      </button>
    </div>
  );
}
